import { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { CycleRing } from '@/lib/cycleTypes';
import type { RingTheme } from '@/lib/ringThemeStore';
import { Plus, Sparkles, Trash2 } from 'lucide-react';

type RingThemeWizardProps = {
  open: boolean;
  onClose: () => void;
  themes: RingTheme[];
  activeThemeId?: string;
  rings: CycleRing[];
  onSaveTheme: (theme: RingTheme) => void;
  onDeleteTheme: (themeId: string) => void;
  onApplyThemeToRing: (themeId: string, ringId: string) => void;
  onApplyThemeLayout: (themeId: string) => void;
  onApplyThemeOrder: (themeId: string) => void;
};

const parseColors = (value: string) =>
  value
    .split(',')
    .map((color) => color.trim())
    .filter(Boolean);

export function RingThemeWizard({
  open,
  onClose,
  themes,
  activeThemeId,
  rings,
  onSaveTheme,
  onDeleteTheme,
  onApplyThemeToRing,
  onApplyThemeLayout,
  onApplyThemeOrder,
}: RingThemeWizardProps) {
  const [editingThemeId, setEditingThemeId] = useState<string | null>(null);
  const [selectedRingId, setSelectedRingId] = useState<string>('');

  const editingTheme = useMemo(
    () => themes.find((theme) => theme.id === editingThemeId) ?? null,
    [themes, editingThemeId]
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [colorsInput, setColorsInput] = useState('');
  const [ringWidth, setRingWidth] = useState<string>('');
  const [ringGap, setRingGap] = useState<string>('');
  const [visibleRingCount, setVisibleRingCount] = useState<string>('');
  const [ringOrder, setRingOrder] = useState<string[] | undefined>(undefined);

  const resetForm = () => {
    setEditingThemeId(null);
    setName('');
    setDescription('');
    setColorsInput('');
    setRingWidth('');
    setRingGap('');
    setVisibleRingCount('');
    setRingOrder(undefined);
  };

  const populateForm = (theme: RingTheme) => {
    setName(theme?.name ?? '');
    setDescription(theme.description ?? '');
    setColorsInput(theme.colors.join(', '));
    setRingWidth(theme.ringWidth ? String(theme.ringWidth) : '');
    setRingGap(theme.ringGap ? String(theme.ringGap) : '');
    setVisibleRingCount(theme.visibleRingCount ? String(theme.visibleRingCount) : '');
    setRingOrder(theme.ringOrder);
  };

  const handleEdit = (theme: RingTheme) => {
    setEditingThemeId(theme.id);
    populateForm(theme);
  };

  const handleCreateNew = () => {
    resetForm();
    setEditingThemeId('new');
  };

  const handleSave = () => {
    const colors = parseColors(colorsInput);
    if (!name.trim() || !colors.length) return;

    const nextTheme: RingTheme = {
      id: editingThemeId && editingThemeId !== 'new' ? editingThemeId : `theme-${Date.now()}`,
      name: name.trim(),
      description: description.trim() || undefined,
      colors,
      ringWidth: ringWidth ? Number(ringWidth) : undefined,
      ringGap: ringGap ? Number(ringGap) : undefined,
      visibleRingCount: visibleRingCount ? Number(visibleRingCount) : undefined,
      ringOrder,
    };

    onSaveTheme(nextTheme);
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            Ring Theme Studio
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-6 md:grid-cols-[1.1fr_1fr]">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Saved Themes</p>
              <Button variant="outline" size="sm" onClick={handleCreateNew}>
                <Plus className="w-3 h-3 mr-1" />
                New Theme
              </Button>
            </div>

            <div className="space-y-2 max-h-[360px] overflow-auto pr-1">
              {themes.length ? (
                themes.map((theme) => (
                  <div
                    key={theme.id}
                    className="rounded-lg border border-border/50 bg-background/40 p-3 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold">{theme.name}</p>
                        {theme.description && (
                          <p className="text-xs text-muted-foreground">{theme.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant={activeThemeId === theme.id ? 'secondary' : 'ghost'}
                          size="sm"
                          onClick={() => onApplyThemeLayout(theme.id)}
                        >
                          {activeThemeId === theme.id ? 'Active layout' : 'Apply layout'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onApplyThemeOrder(theme.id)}
                          disabled={!theme.ringOrder?.length}
                        >
                          Apply order
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(theme)}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => onDeleteTheme(theme.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {theme.colors.map((color, index) => (
                        <span
                          key={`${theme.id}-color-${index}`}
                          className="h-4 w-4 rounded-full border border-border/40"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                      {theme.visibleRingCount && (
                        <span>{theme.visibleRingCount} rings visible</span>
                      )}
                      {theme.ringWidth && <span>Width {theme.ringWidth}px</span>}
                      {theme.ringGap && <span>Gap {theme.ringGap}px</span>}
                      {theme.ringOrder?.length && <span>{theme.ringOrder.length} ring order slots</span>}
                    </div>
                    <div className="flex flex-col gap-2 md:flex-row md:items-center">
                      <Select
                        value={selectedRingId || undefined}
                        onValueChange={setSelectedRingId}
                      >
                        <SelectTrigger className="h-8 md:w-48">
                          <SelectValue placeholder="Select ring" />
                        </SelectTrigger>
                        <SelectContent>
                          {rings.map((ring) => (
                            <SelectItem key={ring.id} value={ring.id}>
                              {ring.name ?? 'Unnamed'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!selectedRingId}
                        onClick={() => {
                          if (!selectedRingId) return;
                          onApplyThemeToRing(theme.id, selectedRingId);
                        }}
                      >
                        Apply to ring
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-lg border border-dashed border-border/60 p-4 text-sm text-muted-foreground">
                  No ring themes yet. Create a new theme to get started.
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {editingThemeId ? 'Edit theme' : 'Theme builder'}
            </p>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="theme-name">Theme name</Label>
                <Input
                  id="theme-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nebula glow"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="theme-description">Description</Label>
                <Input
                  id="theme-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Soft gradients for lunar overlays"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="theme-colors">Ring colors (comma-separated)</Label>
                <Input
                  id="theme-colors"
                  value={colorsInput}
                  onChange={(e) => setColorsInput(e.target.value)}
                  placeholder="#FBD38D, #9F7AEA, #63B3ED"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="theme-ring-width">Ring width</Label>
                  <Input
                    id="theme-ring-width"
                    type="number"
                    value={ringWidth}
                    onChange={(e) => setRingWidth(e.target.value)}
                    placeholder="22"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="theme-ring-gap">Ring gap</Label>
                  <Input
                    id="theme-ring-gap"
                    type="number"
                    value={ringGap}
                    onChange={(e) => setRingGap(e.target.value)}
                    placeholder="3"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="theme-ring-count">Visible rings</Label>
                  <Input
                    id="theme-ring-count"
                    type="number"
                    value={visibleRingCount}
                    onChange={(e) => setVisibleRingCount(e.target.value)}
                    placeholder="8"
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const ordered = [...rings]
                      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                      .map((ring) => ring.id);
                    setRingOrder(ordered);
                  }}
                >
                  Capture current ring order
                </Button>
                {ringOrder?.length ? (
                  <span className="text-xs text-muted-foreground">
                    {ringOrder.length} rings stored
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">No ring order saved</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={handleSave} disabled={!name.trim() || !parseColors(colorsInput).length}>
                  Save theme
                </Button>
                <Button variant="ghost" onClick={resetForm}>
                  Clear
                </Button>
              </div>
              {editingTheme && (
                <p className="text-xs text-muted-foreground">
                  Editing: <span className="text-foreground">{editingTheme.name}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
